/**
 * Portfolios → Tasks subpage.
 *
 * Mirrors Projects → Tasks visually and behaviorally:
 *   - Standard view: ONE unified ShadCN table containing portfolio-level tasks
 *     (PortfolioTaskCN) AND every per-location project task (TaskCN). A "Location"
 *     column distinguishes them ("—" for portfolio, location.assetName for per-location).
 *   - Calendar view: month grid over the same aggregated set.
 *   - Timeline view: horizontal timeline over the same aggregated set.
 *
 * Editing: clicking any row opens the polymorphic <TaskCNDetailSheet>. Click routing
 * uses the presence of `task.projectid` to decide `taskKind` ('taskcn' for per-location,
 * 'portfoliotask' for portfolio-level). Sheet edits flow through the appropriate slice;
 * a Redux overlay on `state.taskCNs.entities` keeps per-location rows fresh after edits
 * without a refetch.
 *
 * Templates affordances (Download / Create) operate on portfolio tasks only.
 */

import { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import AssigneePopover from 'components/AssigneePopover/AssigneePopover'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import { Input } from 'components/ui/input'
import { Select } from 'components/ui/select'
import { Switch } from 'components/ui/switch'
import { Button } from 'components/ui/button'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from 'components/ui/table'
import SortableTasksTableBody from 'components/TasksList/SortableTasksTableBody'
import { TableSortIcon } from 'components/TableSortIcon'
import { TablePagination } from 'components/ui/pagination'
import useIsMobile from 'hooks/useIsMobile'
import {
	formatScheduledTime,
	filterTasks,
	sortTaskList,
	collectAssignees,
	collectProjects,
	isOverdue,
} from 'utils/tasks'
import { canCompleteTask } from 'utils/taskPermissions'
import { cn } from 'lib/utils'
import { toast } from 'components/ui/toast-store'
import { ArrowRight } from 'lucide-react'
import {
	Search,
	Filter,
	Table2,
	Calendar as CalendarIcon,
	GitBranch,
	Download,
	LayoutTemplate,
	Plus,
	Eye,
	Check,
	Calendar,
	ListTodo,
} from 'lucide-react'
import TasksCalendar from 'components/TasksCalendar'
import TasksTimeline from 'components/TasksTimeline'
import TaskTemplatesSelectCN from 'components/TaskTemplatesSelectCN'
import BuildTemplateModal from 'components/popups/BuildTemplateModal'
import TaskCNDetailSheet from 'screens/taskscn/TaskCNDetailSheet'
import Popup from 'components/Popup'
import {
	fetchPortfolioTasks,
	createPortfolioTask,
	updatePortfolioTask,
	selectPortfolioTasks,
} from 'slices/portfolioTaskCNSlice'
import {
	initializeTemplates,
	initializeTemplateTasks,
	buildTemplateFromTasks,
} from 'slices/templateSlice'
import { updateTaskCN, reorderTaskCNs } from 'slices/taskCNSlice'
import type { PortfolioTask } from 'types/portfolioTask'
import CreatePortfolioTaskModal from '../components/CreatePortfolioTaskModal'

// ─── Visual / utility helpers (mirror ProjectTasksCN) ─────────────────────────

const PRIORITY_STYLES: Record<string, { text: string; bg: string; border: string }> = {
	'P1 - Urgent': { text: 'rgb(239, 68, 68)', bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.2)' },
	'P2 - High': { text: 'rgb(249, 115, 22)', bg: 'rgba(249, 115, 22, 0.1)', border: 'rgba(249, 115, 22, 0.2)' },
	'P3 - Medium': { text: 'rgb(62, 124, 177)', bg: 'rgba(62, 124, 177, 0.1)', border: 'rgba(62, 124, 177, 0.2)' },
	'P4 - Low': { text: 'rgb(113, 113, 122)', bg: 'rgba(113, 113, 122, 0.1)', border: 'rgba(113, 113, 122, 0.2)' },
}

function PriorityBadge({ priority }: { priority?: string }) {
	if (!priority) return <span className="text-gray-400">-</span>
	const label = priority.split(' - ')[0] || priority
	const style = PRIORITY_STYLES[priority]
	if (!style) return <span className="text-gray-400">{label}</span>
	return (
		<span
			className="inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-semibold border"
			style={{ color: style.text, backgroundColor: style.bg, borderColor: style.border }}
		>
			{label}
		</span>
	)
}

function EffortBadge({ effort }: { effort?: string }) {
	if (!effort) return <span className="text-gray-400">-</span>
	const label = effort.split(' - ')[0] || effort
	return <span className="text-sm text-gray-500">{label}</span>
}

function formatDueDate(dateStr?: string | null): string {
	if (!dateStr) return '-'
	const d = new Date(dateStr)
	const taskDate = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
	const today = new Date()
	today.setHours(0, 0, 0, 0)
	const tomorrow = new Date(today)
	tomorrow.setDate(tomorrow.getDate() + 1)
	if (taskDate.getTime() === today.getTime()) return 'Today'
	if (taskDate.getTime() === tomorrow.getTime()) return 'Tomorrow'
	return taskDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function TaskCheckbox({
	checked,
	onChange,
	disabled = false,
	title,
}: {
	checked: boolean
	onChange: () => void
	disabled?: boolean
	title?: string
}) {
	return (
		<button
			type="button"
			disabled={disabled}
			title={title}
			onClick={e => {
				e.stopPropagation()
				onChange()
			}}
			className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
				checked ? 'bg-[#0080fe] border-[#0080fe] text-white' : 'bg-transparent border-gray-300 hover:border-[#0080fe]'
			} ${disabled ? 'opacity-40 cursor-not-allowed hover:border-gray-300' : ''}`}
		>
			{checked && <Check className="w-2.5 h-2.5" />}
		</button>
	)
}

// Column definitions, mirroring the main Tasks screen plus the Location column
// this aggregated view adds. The leading `dragHandle` slot reserves space for
// the grip column rendered by SortableTasksTableBody.
const headerCells = [
	{ id: 'dragHandle', label: '', sortable: false },
	{ id: 'completed', label: '', sortable: false },
	{ id: 'taskName', label: 'Task', sortable: true },
	{ id: '_locationName', label: 'Location', sortable: true },
	{ id: 'assignedTo', label: 'Assigned', sortable: true },
	{ id: 'taskPriority', label: 'Priority', sortable: true },
	{ id: 'taskEffort', label: 'Effort', sortable: true },
	{ id: 'dateDue', label: 'Due Date', sortable: true },
	{ id: 'time', label: 'Time', sortable: false },
	{ id: 'actions', label: '', sortable: false },
]

// ─── Types ────────────────────────────────────────────────────────────────────

type LocationTask = {
	_id: string
	taskName: string
	taskPriority?: string
	taskEffort?: string
	taskStatus?: string
	description?: string
	dateDue?: string | null
	dateCompleted?: string | null
	completed?: boolean
	updatedAt?: string
	createdAt?: string
	assignedTo?: any
	userid?: any
	isDeleted?: boolean
	projectid?: { _id?: string; projectAsset?: { _id?: string; assetName?: string; image?: string } }
}

type LocationLite = { _id: string; image?: string | null; assetName: string }
type ViewMode = 'standard' | 'calendar' | 'timeline'
type SheetKind = 'taskcn' | 'portfoliotask'

const withCompletedBy = <T extends { dateDue?: string | null }>(t: T): T & { completedBy: string } => ({
	...t,
	completedBy: (t.dateDue as string) || '',
})

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
	return (
		<div className="flex flex-col items-center justify-center py-16">
			<div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-gray-100">
				<ListTodo className="w-8 h-8 text-gray-400" />
			</div>
			<h2 className="text-lg font-semibold text-gray-900 mb-1">No tasks found</h2>
			<p className="text-sm text-gray-500 mb-4">Create your first portfolio task to get started</p>
			<Button onClick={onCreateClick}>
				<Plus className="w-4 h-4 mr-1" />
				Add Portfolio Task
			</Button>
		</div>
	)
}

// ─── Component ────────────────────────────────────────────────────────────────

const PortfolioTasksCN = () => {
	const dispatch = useDispatch()
	const { id } = useParams<{ id: string }>()
	const portfolioId = id!

	const userLogin = useSelector((state: any) => state.userLogin)
	const userInfo = userLogin?.userInfo
	const portfolio = useSelector((state: any) => state.portfolios?.entities?.[portfolioId])

	// View mode + page filters
	const [viewMode, setViewMode] = useState<ViewMode>('standard')
	const [searchQuery, setSearchQuery] = useState('')
	const [filterPriority, setFilterPriority] = useState('')
	const [filterProject, setFilterProject] = useState('')
	const [filterAssignee, setFilterAssignee] = useState('')
	const [showCompleted, setShowCompleted] = useState(false)

	// Sorting state. Due Date descending matches the previous fixed sort.
	const [orderBy, setOrderBy] = useState<string | null>('dateDue')
	const [order, setOrder] = useState<'asc' | 'desc'>('desc')

	// Pagination state
	const [paginationEnabled, setPaginationEnabled] = useState(false)
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(10)

	// Sheet state — single task pointer + kind, no separate modal selection
	const [sheetTask, setSheetTask] = useState<any>(null)
	const [sheetKind, setSheetKind] = useState<SheetKind>('taskcn')

	// Modals
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [openTemplateLoad, setOpenTemplateLoad] = useState(false)
	const [openTemplateBuild, setOpenTemplateBuild] = useState(false)

	// Assignee popover
	const [userPopup, setUserPopup] = useState<{ user: any; anchor: DOMRect } | null>(null)

	// ─── Data: portfolio tasks (slice) ──────────────────────────────────────────
	const portfolioTasks = useSelector((s: any) => selectPortfolioTasks(s, portfolioId))
	useEffect(() => {
		if (portfolioId) {
			dispatch(fetchPortfolioTasks(portfolioId) as any)
		}
	}, [dispatch, portfolioId])

	// ─── Data: templates ────────────────────────────────────────────────────────
	const {
		entities: templateEntities,
		ids: templateIds,
		isInitialized: templatesInitialized,
	} = useSelector((s: any) => s.templates)
	useEffect(() => {
		if (!templatesInitialized) {
			dispatch(initializeTemplates() as any)
		}
	}, [dispatch, templatesInitialized])
	const taskTemplates = useMemo(
		() =>
			(Array.isArray(templateIds) ? templateIds.map((tid: string) => templateEntities[tid]) : [])
				.filter(Boolean)
				.filter((t: any) => t?.templateType === 'task'),
		[templateIds, templateEntities],
	)

	// ─── Locations from the portfolio ───────────────────────────────────────────
	const locationsList: LocationLite[] = useMemo(() => {
		const raw = portfolio?.locations || []
		return raw
			.map((entry: any) => entry?.location)
			.filter(Boolean)
			.map((loc: any) => ({
				_id: loc._id,
				image: loc.image,
				assetName: loc.assetName || '',
			}))
	}, [portfolio?.locations])

	// ─── Per-location task fetch (raw axios into local state) ───────────────────
	const [tasksByLocationId, setTasksByLocationId] = useState<Record<string, LocationTask[]>>({})

	useEffect(() => {
		if (!portfolio?._id || locationsList.length === 0) {
			setTasksByLocationId({})
			return
		}
		let cancelled = false
		const token = userInfo?.token
		Promise.all(
			locationsList.map(async loc => {
				try {
					const { data } = await axios.get('/api/taskcns/bylocation', {
						params: { locationid: loc._id },
						headers: token ? { Authorization: `Bearer ${token}` } : undefined,
					})
					return [loc._id, (data || []) as LocationTask[]] as const
				} catch {
					return [loc._id, [] as LocationTask[]] as const
				}
			}),
		).then(results => {
			if (cancelled) return
			setTasksByLocationId(Object.fromEntries(results))
		})
		return () => {
			cancelled = true
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [portfolio?._id, locationsList.length, userInfo?.token])

	// ─── Redux overlay: sheet edits (TaskCN slice) propagate without refetch ─
	const taskCNEntities = useSelector((s: any) => s.taskCNs?.entities)
	const liveTasksByLocationId = useMemo(() => {
		const out: Record<string, LocationTask[]> = {}
		for (const [locId, tasks] of Object.entries(tasksByLocationId)) {
			out[locId] = tasks
				.map(t => (taskCNEntities?.[t._id] as LocationTask | undefined) ?? t)
				.filter(t => !t?.isDeleted)
		}
		return out
	}, [tasksByLocationId, taskCNEntities])

	// Quick lookup: locationId → assetName, for the Location column
	const locationNameById = useMemo(() => {
		const m = new Map<string, string>()
		for (const loc of locationsList) m.set(loc._id, loc.assetName)
		return m
	}, [locationsList])

	// Decorate per-location tasks with their location name (for the column + sort)
	const locationTasksFlat = useMemo(() => {
		const out: (LocationTask & { _locationName?: string })[] = []
		for (const [locId, tasks] of Object.entries(liveTasksByLocationId)) {
			const name = locationNameById.get(locId) || ''
			for (const t of tasks) out.push({ ...t, _locationName: name })
		}
		return out
	}, [liveTasksByLocationId, locationNameById])

	// ─── Aggregated, filtered, sorted task list ─────────────────────────────────
	const allTasks = useMemo(
		() => [...portfolioTasks, ...locationTasksFlat] as any[],
		[portfolioTasks, locationTasksFlat],
	)

	// Filter options are derived from the tasks in hand so each dropdown only
	// offers values that can actually match a row.
	const assigneeOptions = useMemo(() => collectAssignees(allTasks), [allTasks])
	const projectOptions = useMemo(() => collectProjects(allTasks), [allTasks])

	const filteredTasks = useMemo(
		() => filterTasks(allTasks, { searchQuery, filterPriority, filterProject, filterAssignee, showCompleted }),
		[allTasks, searchQuery, filterPriority, filterProject, filterAssignee, showCompleted],
	)

	const sortedTasks = useMemo(() => sortTaskList(filteredTasks, orderBy, order), [filteredTasks, orderBy, order])

	// Mobile lists are unpaginated and scrolled. Coerced for rendering and
	// never written back, so a preference set on a desktop survives a visit
	// from a phone.
	const isMobile = useIsMobile()
	const showPagination = paginationEnabled && !isMobile

	const paginatedTasks = useMemo(() => {
		if (!showPagination) return sortedTasks
		const start = page * rowsPerPage
		return sortedTasks.slice(start, start + rowsPerPage)
	}, [sortedTasks, page, rowsPerPage, showPagination])

	// Reset to the first page whenever the visible set or its order changes.
	useEffect(() => {
		setPage(0)
	}, [searchQuery, filterPriority, filterProject, filterAssignee, showCompleted, orderBy, order, paginationEnabled])

	const handleSort = (columnId: string) => {
		if (orderBy === columnId) {
			setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(columnId)
			setOrder('asc')
		}
	}

	const aggregatedForCalendarTimeline = useMemo(
		() => filteredTasks.map(withCompletedBy),
		[filteredTasks],
	)

	const hasAnyTasks = portfolioTasks.length > 0 || locationTasksFlat.length > 0

	// ─── Click + edit handlers ─────────────────────────────────────────────────
	const openTaskInSheet = useCallback((task: any) => {
		setSheetTask(task)
		setSheetKind(task?.projectid ? 'taskcn' : 'portfoliotask')
	}, [])

	const handleSheetClose = useCallback(() => {
		setSheetTask(null)
	}, [])

	const handleAvatarClick = useCallback((e: React.MouseEvent, user: any) => {
		e.stopPropagation()
		const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
		setUserPopup({ user, anchor: rect })
	}, [])

	// Completing a task is limited to its creator, its assignee and the owner of
	// the portfolio (or, for a location task, its project). See utils/taskPermissions.
	const canComplete = useCallback(
		(task: any) => canCompleteTask(task, userInfo?._id, [portfolio?.owner]),
		[userInfo, portfolio],
	)

	const handleToggleComplete = useCallback(
		async (task: any) => {
			if (task.completed || !canComplete(task)) return
			const confirmed = await toast.confirm({
				title: 'Complete Task',
				message: `Mark "${task.taskName}" as completed?`,
				confirmLabel: 'Set as Completed',
				cancelLabel: 'Cancel',
				variant: 'default',
			})
			if (!confirmed) return
			const completePatch = { completed: true, dateCompleted: new Date().toISOString() }
			if (task?.projectid) {
				dispatch(updateTaskCN({ _id: task._id, ...completePatch }) as any)
			} else {
				dispatch(
					updatePortfolioTask({
						taskId: task._id,
						portfolioId,
						patch: completePatch,
					}) as any,
				)
			}
		},
		[dispatch, portfolioId, canComplete],
	)

	const handleCalendarDateChange = useCallback(
		(task: any, newDate: string) => {
			if (task?.projectid) {
				dispatch(updateTaskCN({ _id: task._id, dateDue: newDate }) as any)
			} else {
				dispatch(
					updatePortfolioTask({
						taskId: task._id,
						portfolioId,
						patch: { dateDue: newDate },
					}) as any,
				)
			}
		},
		[dispatch, portfolioId],
	)

	// ─── Templates ──────────────────────────────────────────────────────────────
	const handleLoadTemplate = async (templateId: string) => {
		try {
			const result: any = await dispatch(initializeTemplateTasks(templateId) as any)
			const tasks: any[] = Array.isArray(result?.payload) ? result.payload : []
			for (const t of tasks) {
				await dispatch(
					createPortfolioTask({
						portfolioid: portfolioId,
						taskName: t.taskName,
						description: t.description,
						taskPriority: t.priority,
						taskEffort: t.effort,
					}) as any,
				)
			}
		} finally {
			setOpenTemplateLoad(false)
		}
	}

	const portfolioTasksForBuilder = useMemo(
		() =>
			filterTasks(portfolioTasks as any[], { searchQuery, filterPriority, filterAssignee, showCompleted }).map(
				(t: PortfolioTask) => ({
					_id: t._id,
					taskName: t.taskName,
					taskPriority: t.taskPriority,
					taskEffort: t.taskEffort,
				}),
			),
		[portfolioTasks, searchQuery, filterPriority, filterAssignee, showCompleted],
	)

	const handleBuildTemplate = async (data: any) => {
		try {
			await dispatch(buildTemplateFromTasks(data) as any)
			setOpenTemplateBuild(false)
			dispatch(initializeTemplates() as any)
		} catch {
			// templateSlice surfaces error state; modal stays open
		}
	}

	// ─── Guard ──────────────────────────────────────────────────────────────────
	if (!portfolio) return null

	return (
		<div>
			{/* Header */}
			<div className="flex flex-wrap justify-between items-center gap-4 mb-6">
				<div className="flex flex-col gap-1">
					<h2 className="text-2xl font-bold m-0">Tasks</h2>
					<span className="text-sm text-gray-600">
						Manage tasks across this portfolio. Portfolio-level and location project tasks are unified below.
					</span>
					<div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-0.5 mt-2 w-fit">
						<button
							type="button"
							onClick={() => setViewMode('standard')}
							className={`flex items-center space-x-1 px-2.5 py-1.5 rounded-md transition-all text-xs ${
								viewMode === 'standard' ? 'bg-white text-[#0080fe] shadow-sm' : 'text-gray-500 hover:text-[#0080fe]'
							}`}
							title="Table view"
						>
							<Table2 className="w-3 h-3" />
							<span className="font-medium">List</span>
						</button>
						<button
							type="button"
							onClick={() => setViewMode('calendar')}
							className={`flex items-center space-x-1 px-2.5 py-1.5 rounded-md transition-all text-xs ${
								viewMode === 'calendar' ? 'bg-white text-[#0080fe] shadow-sm' : 'text-gray-500 hover:text-[#0080fe]'
							}`}
							title="Calendar view"
						>
							<CalendarIcon className="w-3 h-3" />
							<span className="font-medium">Calendar</span>
						</button>
						<button
							type="button"
							onClick={() => setViewMode('timeline')}
							className={`flex items-center space-x-1 px-2.5 py-1.5 rounded-md transition-all text-xs ${
								viewMode === 'timeline' ? 'bg-white text-[#0080fe] shadow-sm' : 'text-gray-500 hover:text-[#0080fe]'
							}`}
							title="Timeline view"
						>
							<GitBranch className="w-3 h-3" />
							<span className="font-medium">Timeline</span>
						</button>
					</div>
				</div>
				{/* Add Portfolio Task and Download Template are always available:
				    an empty task list is exactly when you want to add the first
				    task or seed it from a template. */}
				<div className="flex items-center gap-3 flex-wrap">
					<Button onClick={() => setShowCreateModal(true)}>
						<Plus className="w-4 h-4 mr-1" />
						<span>Add Portfolio Task</span>
					</Button>
					<Button variant="ghost" onClick={() => setOpenTemplateLoad(true)}>
						<Download className="w-4 h-4 mr-1" />
						<span>Download Template</span>
					</Button>
					{/* Create Template builds a template FROM the tasks on this
					    page, so it stays hidden until there is at least one. */}
					{hasAnyTasks && (
						<Button variant="ghost" onClick={() => setOpenTemplateBuild(true)}>
							<LayoutTemplate className="w-4 h-4 mr-1" />
							<span>Create Template</span>
						</Button>
					)}
				</div>
			</div>

			{viewMode === 'standard' ? (
				<>
					{/* Filter bar */}
					<div className="py-3 border-b border-gray-200 flex items-center gap-4 flex-wrap mb-4">
						<div className="relative flex-1 min-w-[200px] max-w-md">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
							<Input
								type="text"
								placeholder="Search tasks..."
								value={searchQuery}
								onChange={e => setSearchQuery(e.target.value)}
								className="pl-9"
							/>
						</div>
						<div className="flex items-center gap-2 flex-wrap">
							<Filter className="w-3.5 h-3.5 text-gray-400" />
							<Select
								value={filterPriority}
								onChange={e => setFilterPriority(e.target.value)}
								className="w-[150px]"
							>
								<option value="">All Priorities</option>
								<option value="P1 - Urgent">P1 - Urgent</option>
								<option value="P2 - High">P2 - High</option>
								<option value="P3 - Medium">P3 - Medium</option>
								<option value="P4 - Low">P4 - Low</option>
							</Select>
							<Select
								value={filterProject}
								onChange={e => setFilterProject(e.target.value)}
								className="w-[180px]"
							>
								<option value="">All Projects</option>
								{projectOptions.map(p => (
									<option key={p.id} value={p.id}>
										{p.label}
									</option>
								))}
							</Select>
							<Select
								value={filterAssignee}
								onChange={e => setFilterAssignee(e.target.value)}
								className="w-[160px]"
							>
								<option value="">All Assignees</option>
								{assigneeOptions.map(a => (
									<option key={a.id} value={a.id}>
										{a.label}
									</option>
								))}
							</Select>
						</div>
						<div className="flex items-center gap-4 ml-auto">
							{/* Hidden on mobile, where lists are always unpaginated and this
							    switch would be a control that does nothing. */}
							{!isMobile && (
							<label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer">
								<Switch checked={paginationEnabled} onChange={e => setPaginationEnabled(e.target.checked)} />
								<span>Enable pagination</span>
							</label>
							)}
							<label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer">
								<Switch checked={showCompleted} onChange={e => setShowCompleted(e.target.checked)} />
								<span>Show completed</span>
							</label>
						</div>
					</div>

					{/* Table */}
					<div className="w-full">
						{!hasAnyTasks ? (
							<EmptyState onCreateClick={() => setShowCreateModal(true)} />
						) : sortedTasks.length > 0 ? (
							<div className="rounded-lg border border-gray-200 overflow-hidden shadow-sm bg-white">
								<Table>
									<TableHeader>
										<TableRow className="bg-gray-50">
											{headerCells.map(cell => (
												<TableHead
													key={cell.id}
													className={cn(
														'text-gray-700 text-xs font-semibold whitespace-nowrap',
														cell.sortable && 'cursor-pointer select-none',
														cell.id === 'dragHandle' && 'w-8',
														cell.id === 'completed' && 'w-10',
														cell.id === 'taskName' && 'min-w-[200px]',
														cell.id === '_locationName' && 'w-40',
														cell.id === 'assignedTo' && 'w-10 text-center',
														cell.id === 'taskPriority' && 'w-24 text-center',
														cell.id === 'taskEffort' && 'w-20 text-center',
														cell.id === 'dateDue' && 'w-28 text-center',
														cell.id === 'time' && 'w-24 text-center',
														cell.id === 'actions' && 'w-10',
													)}
													onClick={cell.sortable ? () => handleSort(cell.id) : undefined}
												>
													<span className="inline-flex items-center">
														{cell.label}
														{cell.sortable && (
															<TableSortIcon columnId={cell.id} orderBy={orderBy} order={order} />
														)}
													</span>
												</TableHead>
											))}
										</TableRow>
									</TableHeader>
									<SortableTasksTableBody
										tasks={paginatedTasks}
										columnCount={headerCells.length}
										orderBy={orderBy}
										// Portfolio-level tasks live in a separate collection and can't be
										// reordered through the TaskCN reorder endpoint -- gate them here.
										isRowDraggable={(t: any) => !!t.projectid}
										rowClassName={(task: any) =>
											`cursor-pointer group ${task.completed ? 'opacity-60' : ''} ${
												sheetTask?._id === task._id ? 'bg-blue-50' : ''
											}`
										}
										onRowClick={(task: any) => openTaskInSheet(task)}
										onReorder={ids => dispatch(reorderTaskCNs(ids) as any)}
										renderRowCells={(task: any) => {
											const overdue = isOverdue(task.dateDue) && !task.completed
											const locationName =
												task._locationName ||
												task.projectid?.projectAsset?.assetName ||
												''
											const isPortfolio = !task.projectid
											const predecessor =
												typeof task.predecessorTaskId === 'object' ? task.predecessorTaskId : null
											return (
												<>
													<TableCell>
														<TaskCheckbox
															checked={!!task.completed}
															onChange={() => handleToggleComplete(task)}
															disabled={!canComplete(task)}
															title={
																canComplete(task)
																	? undefined
																	: 'Only the task creator, the assignee or the owner can complete this task'
															}
														/>
													</TableCell>
													<TableCell>
														<span
															className={`text-sm font-medium ${
																task.completed ? 'line-through text-gray-400' : 'text-gray-900'
															}`}
														>
															{task.taskName}
														</span>
														{task.description && (
															<span className="text-sm text-gray-500 ml-1">- {task.description}</span>
														)}
														{predecessor?.taskName && (
															<span className="ml-2 inline-flex items-center gap-1 text-[11px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
																<ArrowRight className="w-3 h-3" />
																<span title={predecessor.taskName}>follows {predecessor.taskName}</span>
															</span>
														)}
														{task.completed && task.dateCompleted && (
															<span className="ml-2 text-xs text-gray-400">
																Date Completed:{' '}
																{new Date(task.dateCompleted).toLocaleDateString('en-US', {
																	month: '2-digit',
																	day: '2-digit',
																	year: 'numeric',
																})}
															</span>
														)}
													</TableCell>
													<TableCell>
														{isPortfolio ? (
															<span className="text-xs text-gray-400 italic">Portfolio</span>
														) : (
															<span className="text-xs text-gray-700 truncate inline-block max-w-[150px]">
																{locationName || '—'}
															</span>
														)}
													</TableCell>
													<TableCell className="text-center">
														<button
															type="button"
															className="bg-transparent border-none p-0 m-0 cursor-pointer mx-auto block"
															onClick={e => handleAvatarClick(e, task.assignedTo)}
															title={task.assignedTo?.name || task.assignedTo?.firstName || ''}
														>
															<Avatar className="h-7 w-7">
																<AvatarImage
																	src={task.assignedTo?.image}
																	alt={task.assignedTo?.name || ''}
																/>
																<AvatarFallback className="text-xs bg-gray-200 text-gray-600">
																	{(
																		task.assignedTo?.name ||
																		task.assignedTo?.firstName ||
																		'?'
																	)
																		.charAt(0)
																		.toUpperCase()}
																</AvatarFallback>
															</Avatar>
														</button>
													</TableCell>
													<TableCell className="text-center">
														<PriorityBadge priority={task.taskPriority} />
													</TableCell>
													<TableCell className="text-center">
														<EffortBadge effort={task.taskEffort} />
													</TableCell>
													<TableCell className="text-center">
														<span
															className={`text-sm inline-flex items-center gap-1 ${
																overdue ? 'text-red-500' : 'text-gray-500'
															}`}
														>
															{task.dateDue && <Calendar className="w-3 h-3" />}
															{formatDueDate(task.dateDue)}
														</span>
													</TableCell>
													{/* Time: its own column, filled only for a scheduled time (MYD-534) */}
													<TableCell className="text-center text-sm text-gray-500 whitespace-nowrap">
														{task.hasScheduledTime ? formatScheduledTime(task.dateDue) : ''}
													</TableCell>
													<TableCell>
														<button
															className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-[#0080fe]"
															onClick={e => {
																e.stopPropagation()
																openTaskInSheet(task)
															}}
															title="View details"
														>
															<Eye className="w-4 h-4" />
														</button>
													</TableCell>
												</>
											)
										}}
									/>
								</Table>
								{showPagination && sortedTasks.length > 10 && (
									<TablePagination
										page={page}
										rowsPerPage={rowsPerPage}
										count={sortedTasks.length}
										onPageChange={setPage}
										onRowsPerPageChange={setRowsPerPage}
									/>
								)}
							</div>
						) : (
							<div className="text-sm text-gray-500 italic py-8 text-center">
								No tasks match the current filters.
							</div>
						)}
					</div>
				</>
			) : viewMode === 'calendar' ? (
				<div style={{ width: '100%', maxWidth: '100%' }}>
					<TasksCalendar
						tasks={aggregatedForCalendarTimeline as any}
						onTaskClick={openTaskInSheet}
						onTaskDateChange={handleCalendarDateChange}
					/>
				</div>
			) : (
				<div style={{ width: '100%', maxWidth: '100%' }}>
					<TasksTimeline
						tasks={aggregatedForCalendarTimeline as any}
						onTaskClick={openTaskInSheet}
						onTaskDateChange={handleCalendarDateChange}
					/>
				</div>
			)}

			{/* Polymorphic detail sheet — handles both portfolio and per-location tasks */}
			<TaskCNDetailSheet
				task={sheetTask}
				open={!!sheetTask}
				onClose={handleSheetClose}
				taskKind={sheetKind}
				portfolioId={portfolioId}
			/>

			{/* Create new portfolio task (button only — sheet handles edits) */}
			<CreatePortfolioTaskModal
				open={showCreateModal}
				onClose={() => setShowCreateModal(false)}
				portfolioId={portfolioId}
			/>

			{/* User Info Popup (assignee avatar click) */}
			{userPopup && (
				<AssigneePopover user={userPopup.user} anchor={userPopup.anchor} onClose={() => setUserPopup(null)} />
			)}

			{/* Templates: Download */}
			{openTemplateLoad && (
				<Popup
					title="Load Tasks Template"
					openPopup={openTemplateLoad}
					setOpenPopup={setOpenTemplateLoad}
					icon={<LayoutTemplate className="w-5 h-5" />}
					description="Browse and apply task templates"
				>
					<TaskTemplatesSelectCN
						taskTemplates={taskTemplates}
						handleTamplateLoad={handleLoadTemplate}
					/>
				</Popup>
			)}

			{/* Templates: Create from current portfolio tasks */}
			{openTemplateBuild && (
				<Popup
					title="Tasks Template"
					openPopup={openTemplateBuild}
					setOpenPopup={setOpenTemplateBuild}
					popupWidth="lg"
					icon={<LayoutTemplate className="w-5 h-5" />}
					description="Create a new task template from this portfolio's tasks"
				>
					<BuildTemplateModal
						tasks={portfolioTasksForBuilder}
						openPopup={openTemplateBuild}
						setOpenPopup={setOpenTemplateBuild}
						onBuildTemplate={handleBuildTemplate}
					/>
				</Popup>
			)}
		</div>
	)
}

export default PortfolioTasksCN
